import {useGetDomainsQuery} from '../../api';
import { ContextParams } from '../../navigation/types/navigationContextTypes';
import { BaseDomain } from '../../ontology/types/domainTypes';
import useActiveOrgData from './useActiveOrgData';
import { createSelector } from '@reduxjs/toolkit';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useParams } from 'react-router-dom';

type QueryResponse = Omit<ReturnType<typeof useGetDomainsQuery>, 'refetch'>;

type ActiveDomainName = string | undefined;

const selectActiveDomain = createSelector(
	(res: QueryResponse) => res.data,
	(res: QueryResponse, activeDomainName: ActiveDomainName) =>
		activeDomainName,
	(data: QueryResponse['data'], activeDomainName: ActiveDomainName) =>
		data?.find((domain: BaseDomain) => domain.name === activeDomainName) ??
		null
);

/**
 * @returns RTK Query data with an activeItem property that corresponds to the data for the domain specified
 * in the application URL.
 */
const useActiveDomainData = (domainName?: string) => {
	const params = useParams() as ContextParams;

	const { activeItem: activeOrg } = useActiveOrgData();

	const fetchData = useGetDomainsQuery(
		activeOrg ? { orgId: activeOrg._id } : skipToken,
		{
			selectFromResult: (res) => ({
				...res,
				activeItem: selectActiveDomain(
					res,
					params.domain || domainName
				) as null | BaseDomain,
			}),
		}
	);

	return fetchData;
};

export default useActiveDomainData;
